/**
 * NotificationListener.js:
 * Dynamically listens to new notifications from Firestore,
 * shows them as temporary Snackbar popups (only once),
 * and stores them in the global context to be viewed in NotificationBell.
 */

// ===============================
// Imports
// ===============================
import React, { useEffect, useState } from "react";
import { db } from "../firebase/firebase";
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
} from "firebase/firestore";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";

import { useNotifications } from "../contexts/NotificationsContext";
import "./NotificationListener.css";

// ===============================
// Component: NotificationListener
// Listens to Firestore notifications for the current user,
// adds them to global context, and shows temporary snackbars.
// ===============================
const NotificationListener = ({ currentUserId }) => {
    // Local state to manage currently visible snackbars
    const [snackbarNotifications, setSnackbarNotifications] = useState([]);
    const [shownSnackbarIds, setShownSnackbarIds] = useState([]);

    // Access global notification context functions & state
    const { addNotification, updateNotification, notifications } = useNotifications();

    useEffect(() => {
        if (!currentUserId) return; // Skip if user is not logged in

        // Query notifications in Firestore where 'userId' matches the current user
        const q = query(
            collection(db, "notifications"),
            where("userId", "==", currentUserId)
        );

        // Realtime listener for changes to this query
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            // Get list of IDs of notifications that were already shown (from localStorage)
            const shownNotifications =
                JSON.parse(localStorage.getItem("shownNotifications")) || [];

            // Process both *new* and *modified* notifications
            const changes = snapshot.docChanges();

            await Promise.all(
                changes.map(async (change) => {
                    const id = change.doc.id;
                    const data = change.doc.data();
                    const { senderImage, fromUserId, type } = data;

                    // Default sender name
                    let senderName = "מישהו";
                    try {
                        const userDoc = await getDoc(doc(db, "users", fromUserId));
                        if (userDoc.exists()) {
                            senderName = userDoc.data().title || "משתמש";
                        }
                    } catch (e) {
                        console.error("Error fetching user name:", e);
                    }

                    let message = data.message || `${senderName} רוצה להצטרף אליך לטיול`;
                    if (!data.message) {
                        if (data.type === "group_join_request") {
                            message = `${senderName} רוצה להצטרף לטיול הקבוצתי שלך`;
                        } else if (data.type === "join_approved_single") {
                            message = `${senderName} אישר את הבקשה שלך להצטרף לטיול`;
                        } else if (data.type === "join_approved_group") {
                            message = `${senderName} אישר את הבקשה שלך להצטרף לטיול הקבוצתי`;
                        }
                    }

                    if (change.type === "added") {
                        addNotification({
                            id,
                            message,
                            fromUserId,
                            senderName,
                            senderImage,
                            type,
                            tripId: data.tripId,
                            timestamp: Date.now(),
                            isRead: shownNotifications.includes(id)
                        });
                        // Show as Snackbar only for new notifications, except for the special rejection message
                        if (
                            message !== "בקשה אחרת להצטרף לטיול זה כבר אושרה" &&
                            !shownSnackbarIds.includes(id)
                        ) {
                            setSnackbarNotifications((prev) => [...prev, { id, message, senderImage }]);
                            setShownSnackbarIds((prev) => [...prev, id]);
                        }
                    } else if (change.type === "modified") {
                        updateNotification({
                            id,
                            message,
                            fromUserId,
                            senderName,
                            senderImage,
                            type,
                            tripId: data.tripId,
                            timestamp: Date.now(),
                            isRead: shownNotifications.includes(id)
                        });
                        // Do NOT show Snackbar for modified notifications
                    }
                })
            );
        });

        // Cleanup Firestore listener on unmount
        return () => unsubscribe(); // Cleanup listener
    }, [currentUserId, addNotification, updateNotification, notifications]);

    const handleClose = (id) => {
        setSnackbarNotifications((prev) => prev.filter((n) => n.id !== id));
        // Optionally, remove from shownSnackbarIds if you want to allow re-showing in the future
    };

    return (
        <>
            {snackbarNotifications.map((notif) => (
                <Snackbar
                    key={notif.id}
                    open
                    autoHideDuration={4000}
                    onClose={() => handleClose(notif.id)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    TransitionComponent={Slide}
                    message={
                        <div className="snackbar-content">
                            {notif.senderImage && (
                                <img
                                    src={notif.senderImage}
                                    alt="sender"
                                    className="snackbar-avatar"
                                />
                            )}
                            <span className="trip-message">{notif.message}</span>
                        </div>
                    }
                    ContentProps={{ className: "custom-snackbar" }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={() => handleClose(notif.id)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                />
            ))}
        </>
    );
};

export default NotificationListener;
